import { useEffect, useMemo } from "react";

import { ModelStatusTypes, type Models } from "@prisma/client";
import { type GetStaticProps, type GetStaticPaths } from "next";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

import { FEATURE_ROUTES } from "@musicfy/components/Navigation/constants";
import PageLoader from "@musicfy/components/PageLoader";
import RecentGenerations from "@musicfy/components/RecentGenerations";
import { type TRecentGeneration } from "@musicfy/components/RecentGenerations/types";
import RVCInference from "@musicfy/components/RVCInference";
import {
  PageMeta,
  absoluteUrl,
  getVoiceModelPath,
  webPageJsonLd,
} from "@musicfy/components/Seo";
import Sidebar from "@musicfy/components/Sidebar";
import { useAudioGenerationsContext } from "@musicfy/libs/AudioGenerationsProvider";
import { useModelsContext } from "@musicfy/libs/ModelsProvider";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { prisma } from "@musicfy/server/db";
import { resolveModelThumbnailSrc } from "@musicfy/utils/modelThumbnail";
import { buildVoicePageTitle } from "@musicfy/utils/voicePageSeo";

// No `userId`. This page is statically generated and publicly cached, so anything in
// these props is served to everyone and is indexable — the owner id of every public
// voice was being shipped in the page payload.
type PublicModel = Omit<Models, "userId">;

interface CreateModelProps {
  model: PublicModel | null;
}

const CreateModel: INextPage<CreateModelProps> = ({ model }) => {
  const { status } = useSession();
  const router = useRouter();

  const { audioGenerations } = useAudioGenerationsContext();

  // Ownership used to come from `model.userId` in the static props. It now comes from
  // the user's own model list, which is fetched per-session and carries `isOwner`.
  const { models, isLoading: areModelsLoading } = useModelsContext();

  const isOwner = useMemo(
    () => !!model && models.some((m) => m.id === model.id && m.isOwner),
    [model, models]
  );

  // Prefer the context row (it has favourites and dataset); fall back to the static
  // props so the voice is still preselected before the models query resolves.
  const initialSelectedModel = useMemo<IModel | null>(() => {
    if (!model) {
      return null;
    }
    const fromContext = models.find((m) => m.id === model.id);
    if (fromContext) {
      return fromContext;
    }
    return { ...model, isOwner, isFavorited: false, dataset: [] };
  }, [model, models, isOwner]);

  const recentAudioGenerations: TRecentGeneration[] = useMemo(() => {
    return audioGenerations
      .filter((ag) => ag.status === "completed")
      .slice(0, 20)
      .map((ag) => {
        return {
          id: ag.id,
          source: ag.url,
          title: ag.model?.artist || "Unknown Artist",
          label: ag.type,
          subtitle: ag.filename,
        };
      });
  }, [audioGenerations]);

  useEffect(() => {
    if (!model?.audioPreviewUrl) {
      return;
    }
    new Audio(model.audioPreviewUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!model) {
      return;
    }
    const cannotAccessPrivateModel =
      (status === "unauthenticated" && model.isPrivate) ||
      (status === "authenticated" &&
        model.isPrivate &&
        !areModelsLoading &&
        !isOwner);

    if (cannotAccessPrivateModel) {
      router.push(FEATURE_ROUTES.voice);
    }
  }, [areModelsLoading, isOwner, model, router, status]);

  const modelImage = resolveModelThumbnailSrc(model?.thumbnail);

  if (model?.isPrivate && !isOwner) {
    return <PageLoader />;
  }

  const voiceTitle = model ? buildVoicePageTitle(model.artist) : undefined;
  const voiceDescription = model
    ? `Create AI ${model.artist} covers as seen on TikTok and YouTube in seconds! Musicfy has hundreds of royalty free voices available for copyright free use.`
    : undefined;
  const voicePath = model ? getVoiceModelPath(model) : undefined;

  return (
    <div>
      {!!model && voiceTitle && voiceDescription && voicePath ? (
        <PageMeta
          title={voiceTitle}
          description={voiceDescription}
          canonicalPath={voicePath}
          ogImage={modelImage}
          ogImageAlt={`${model.artist} thumbnail`}
          ogImageWidth={400}
          ogImageHeight={400}
          ogAudio={model.audioPreviewUrl || undefined}
          robots="index,follow"
          includeFavicon
          jsonLd={webPageJsonLd({
            name: voiceTitle,
            description: voiceDescription,
            url: absoluteUrl(voicePath),
          })}
        />
      ) : null}
      <RVCInference
        initialSelectedModels={
          initialSelectedModel ? [initialSelectedModel] : []
        }
      />
      <Sidebar id="create-model-voice-sidebar">
        <div className="pl-2 text-22 mt-6">Recent Generations:</div>
        <RecentGenerations generations={recentAudioGenerations} />
      </Sidebar>
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const models = await prisma.models.findMany({
      select: {
        artist: true,
      },
      where: {
        status: ModelStatusTypes.completed,
        isPrivate: false,
        isCommunityModel: false,
        isCopyrightFree: false,
      },
      orderBy: {
        usageCount: "desc",
      },
      take: 100,
    });

    return {
      paths: models.map((model) => ({
        params: { voice: [model.artist] },
      })),
      fallback: "blocking",
    };
  } catch (error) {
    // Preview/production builds should not fail because the pooler is briefly
    // unreachable. Blocking fallback still generates each voice page on demand.
    console.error("Skipping voice prerender; database unreachable", error);
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const identifier = params?.voice?.[1];

  if (!identifier) {
    return {
      props: {
        model: null,
      },
    };
  }

  const model = await prisma.models.findFirst({
    // Explicit field list, deliberately excluding `userId` — see PublicModel above.
    // Enumerated rather than spread so a future column has to be opted in to a page
    // that is public, cached and indexed.
    select: {
      id: true,
      artist: true,
      createdAt: true,
      updatedAt: true,
      status: true,
      f0Method: true,
      epochCount: true,
      batchSize: true,
      sampleRate: true,
      version: true,
      isPrivate: true,
      isClusterTrained: true,
      isLocked: true,
      isCopyrightFree: true,
      isInstrumental: true,
      isCommunityModel: true,
      createdBy: true,
      genre: true,
      thumbnail: true,
      modelAId: true,
      modelBId: true,
      usageCount: true,
      audioPreviewUrl: true,
      gender: true,
      trendingUsageCount: true,
    },
    where: {
      OR: [
        {
          artist: identifier,
          status: ModelStatusTypes.completed,
          isCommunityModel: false,
          isPrivate: false,
        },
        {
          id: identifier,
          status: ModelStatusTypes.completed,
        },
      ],
    },
  });

  if (!model) {
    return {
      redirect: {
        destination: "/create/voice",
        permanent: false,
      },
    };
  }

  return {
    props: {
      model: JSON.parse(JSON.stringify(model)) as PublicModel | undefined,
    },
    revalidate: 360,
  };
};

CreateModel.seo = true;

export default CreateModel;
